import { useEffect, useState } from 'react';
import {
  ORDER_STATUS_FLOW,
  ORDER_STATUS_LABEL,
  rollbackTargets,
  vehicleTitle,
  BODY_TYPE_LABEL,
  manages as rolesManage,
  TOWED_KINDS,
  type BodyType,
  type FleetRow,
  type MyCompanyRow,
  type OrderStatus,
  type OrderStatusEventRow,
  type TrackPointRow,
} from 'shared';
import { Money } from '../money';
import { api, type Order } from '../api';
import RouteLine from '../Route';
import Modal from '../Modal';
import type { FileRow } from 'shared';
import { alert, confirm, notify, requestLocation } from '../telegram';
import { useLiveRefresh, useRealtime } from '../realtime';
import TrackMap from '../TrackMap';
import { DownloadButton, FileGrid, UploadButton } from '../Files';
import Expenses from '../Expenses';
import Icon from '../Icon';

/**
 * Рубли учёта → валюта сделки по её собственному, зафиксированному курсу.
 *
 * Не по сегодняшнему: суммы рейса заморожены в момент заключения, и
 * пересчёт свежим курсом показывал бы каждый день новую цифру там, где
 * стоит одна и та же сумма.
 *
 * Так считаются только производные суммы — наценка, выплата, итог к
 * оплате: они по своей природе рублёвые, валюта сделки для них лишь
 * способ показа. Сама договорная ставка не считается, а хранится
 * (`Order.price`) — обратное деление её не воспроизводит.
 */
function toDeal(rub: number, rate: number): number {
  return rate > 0 ? Math.round(rub / rate) : rub;
}

/**
 * Давность последней точки словами.
 *
 * Точный час тут не нужен, нужен ответ на «свежее ли это»: «12 минут
 * назад» и «вчера» человек читает мгновенно, а «02.09 в 23:14» ему
 * приходится вычитать из текущего времени в уме.
 */
function trackAgo(points: TrackPointRow[]): string {
  const last = points[points.length - 1];
  if (!last) return '';

  const min = Math.round((Date.now() - new Date(last.recordedAt).getTime()) / 60_000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;

  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч назад`;
  return `${Math.round(h / 24)} дн назад`;
}

const closed = (s: string) => s === 'DONE' || s === 'CANCELLED';

/*
 * В архив — только по-настоящему закрытые рейсы: отменённые и завершённые
 * с подтверждённой приёмкой. Пока заказчик не подтвердил доставку, сделка
 * открыта — рейс остаётся в «Активных», где его видно обеим сторонам.
 */
const archived = (o: { status: string; confirmedAt: string | null }) =>
  o.status === 'CANCELLED' || (o.status === 'DONE' && !!o.confirmedAt);

/**
 * Производные права и переходы одного рейса — общие для строки в списке
 * и для окна подробностей, и должны совпадать: иначе кнопка в одном месте
 * есть, а в другом для того же рейса пропадает без видимой причины.
 */
function describe(o: Order) {
  const status = o.status as OrderStatus;
  return {
    status,
    // Хвостов больше одного у `ORDER_STATUS_FLOW` не бывает — после
    // отмены остаётся ровно один шаг вперёд или ни одного.
    next: (ORDER_STATUS_FLOW[status] ?? []).filter((s) => s !== 'CANCELLED'),
    back: rollbackTargets(status),
    canMove: o.myRole === 'DRIVER',
    // Диспетчер компании управляет рейсом наравне с водителем, но
    // подменять исполнителя можно только пока машина не в пути.
    canAssign: o.myRole === 'DRIVER' && o.companyId != null,
  };
}

export default function Orders() {
  const [items, setItems] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<'active' | 'archive'>('active');
  const [openId, setOpenId] = useState<string | null>(null);
  const [history, setHistory] = useState<OrderStatusEventRow[]>([]);
  const [track, setTrack] = useState<TrackPointRow[]>([]);
  const [files, setFiles] = useState<FileRow[]>([]);
  const [drivers, setDrivers] = useState<{ id: string; name: string }[]>([]);
  /** Рейс, по которому оформляют перегруз. */
  const [reassign, setReassign] = useState<string | null>(null);
  const [sendingPoint, setSendingPoint] = useState(false);

  /**
   * Разовая отметка «я здесь» из приложения.
   *
   * Постоянную трансляцию ведёт бот — Mini App в фоне геопозицию отдавать
   * не умеет. Но требовать переписки с ботом ради одной точки перед звонком
   * заказчика — перебор, и водители просто не отмечаются вовсе.
   */
  async function sendPoint(orderId: string) {
    setSendingPoint(true);
    try {
      // В Telegram геопозицию отдаёт LocationManager, в браузере —
      // navigator.geolocation; разбор — в `requestLocation`.
      const { lat, lng } = await requestLocation();
      await api.sendTrackPoint(orderId, lat, lng);
      const t = await api.orderTrack(orderId);
      setTrack(t.items);
      notify('success');
    } catch (e) {
      await alert(
        `${(e as Error).message}. Как вариант — включите трансляцию в боте: /track`,
      );
    } finally {
      setSendingPoint(false);
    }
  }

  async function load() {
    setLoading(true);
    try {
      const res = await api.orders();
      setItems(res.items);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();

    // Список водителей нужен только диспетчеру. Ошибку глушим: у обычного
    // пользователя компаний нет, и это нормальный ответ, а не сбой.
    void api
      .myCompanies()
      .then(async (r) => {
        const managed = r.items.find((c) => rolesManage(c.roles));
        if (!managed) return;
        const d = await api.companyDrivers(managed.id);
        setDrivers(d.items);
      })
      .catch(() => {});
  }, []);

  useLiveRefresh(() => void load());

  useRealtime('order:status', (e) => {
    // Статус рейса меняет вторая сторона — заметить это важнее, чем
    // увидеть при следующем открытии экрана.
    notify('success');
    setItems((prev) =>
      prev.map((o) => (o.id === e.orderId ? { ...o, status: e.status } : o)),
    );
    if (openId === e.orderId) void openDetails(e.orderId, true);
  });

  useRealtime('offer:accepted', () => void load());

  useRealtime('order:cancelled', (e) => {
    notify('warning');
    alert(
      `Рейс отменил ${e.byRole === 'DRIVER' ? 'перевозчик' : 'заказчик'}: ${e.byName}` +
        (e.reason ? `\n\nПричина: ${e.reason}` : ''),
    );
    void load();
  });

  async function openDetails(id: string, keepOpen = false) {
    if (openId === id && !keepOpen) {
      setOpenId(null);
      return;
    }
    setOpenId(id);
    try {
      const [h, t, f] = await Promise.all([
        api.orderHistory(id),
        api.orderTrack(id),
        api.orderFiles(id),
      ]);
      setHistory(h.items);
      setTrack(t.items);
      setFiles(f.items);
    } catch (e) {
      alert((e as Error).message);
    }
  }

  /*
   * Машина сдвинулась — дорисовываем отрезок, не перечитывая трек.
   *
   * Событие несёт саму точку: запрашивать весь маршрут на каждом шаге
   * значит за сутки рейса сделать сотни лишних запросов ради одной
   * новой координаты. Точки приходят только по открытому рейсу — по
   * закрытому карта всё равно не показана.
   */
  useRealtime(
    'order:track',
    (e) => {
      if (e.orderId !== openId) return;
      setTrack((prev) => [
        ...prev,
        { lat: e.lat, lng: e.lng, speedKmh: e.speedKmh, recordedAt: e.at },
      ]);
    },
    [openId],
  );

  async function move(order: Order, next: OrderStatus, rollback = false) {
    let comment: string | undefined;

    if (next === 'CANCELLED') {
      if (!(await confirm('Отменить рейс? Вторая сторона получит уведомление.'))) return;
      // Причина обязательна: вторая сторона увидит её в карточке и в боте.
      const reason = window.prompt('Причина отмены (её увидит вторая сторона):')?.trim();
      if (!reason) return alert('Без причины отменить нельзя');
      comment = reason;
    } else if (rollback) {
      comment =
        window.prompt('Почему исправляете статус? (увидит вторая сторона)') ?? undefined;
    }

    try {
      await api.setOrderStatus(order.id, next, comment);
      notify('success');
      await load();
      if (openId === order.id) await openDetails(order.id, true);
    } catch (e) {
      notify('error');
      alert((e as Error).message);
    }
  }

  // Только на первой загрузке: `useLiveRefresh` перечитывает список раз в
  // минуту и на каждом возврате фокуса, и без этого условия уже открытый
  // экран каждый раз гас «Загрузкой…» на время запроса — приложение
  // выглядело мигающим само по себе, хотя рейсы не менялись.
  if (loading && items.length === 0) return <div className="empty">Загрузка…</div>;
  if (error) return <div className="error">{error}</div>;

  const visible = items.filter((o) =>
    tab === 'active' ? !archived(o) : archived(o),
  );
  const archiveCount = items.filter((o) => archived(o)).length;
  const open = items.find((o) => o.id === openId) ?? null;
  const openMeta = open ? describe(open) : null;

  return (
    <>
      <h2>Рейсы</h2>

      <div className="seg" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className={tab === 'active' ? 'seg__btn seg__btn--on' : 'seg__btn'}
          onClick={() => setTab('active')}
        >
          Активные ({items.length - archiveCount})
        </button>
        <button
          type="button"
          className={tab === 'archive' ? 'seg__btn seg__btn--on' : 'seg__btn'}
          onClick={() => setTab('archive')}
        >
          Архив ({archiveCount})
        </button>
      </div>

      {visible.length === 0 && (
        <div className="empty">
          {tab === 'active' ? 'Активных рейсов нет.' : 'Архив пуст.'}
        </div>
      )}

      {visible.map((o) => {
        const counterparty = o.myRole === 'DRIVER' ? o.logist : o.driver;
        const { status, next, canMove, canAssign } = describe(o);
        const fee = o.myRole === 'DRIVER' ? o.sellerFeeRub : o.buyerFeeRub;
        // Отменяет: перевозчик — в любой момент, заказчик — только пока
        // машина не поехала (дальше он уже несёт затраты).
        const canCancel = !closed(status) && (o.myRole === 'DRIVER' || status === 'NEW');

        return (
          <div key={o.id} className="card order">
            <div className="order__grid">
              <div className="order__what">
                <div className="row">
                  <span className="badge">
                    {o.myRole === 'DRIVER' ? 'я везу' : 'я заказчик'}
                  </span>
                  <span className="badge badge--verified">
                    {ORDER_STATUS_LABEL[status] ?? o.status}
                  </span>
                  {o.confirmedAt && (
                    <span className="badge badge--direct">принят заказчиком</span>
                  )}
                </div>

                <div className="route" style={{ marginTop: 8 }}>
                  <RouteLine from={o.cargo.fromAddress} to={o.cargo.toAddress} />
                </div>
                <div className="meta">{o.cargo.title}</div>
              </div>

              <div className="order__money">
                {o.ratesHidden ? (
                  <div className="meta">Суммы по рейсу ведёт диспетчер компании.</div>
                ) : (
                  <>
                    {/* frozen: сумма заключённой сделки. Пересчёт в валюту
                        отображения рядом с ней не показывается — он намекал
                        бы, что цифра может измениться. Она не может. */}
                    <div className="price">
                      <Money
                        frozen
                        currency={o.currency}
                        value={toDeal(
                          o.myRole === 'DRIVER' ? o.driverPayoutRub : o.shipperTotalRub,
                          o.rateToRub,
                        )}
                      />
                    </div>
                    <div className="meta">
                      {o.myRole === 'DRIVER' ? 'вам на руки' : 'к оплате'}
                      {fee !== 0 && (
                        <>
                          {' · ставка '}
                          {/* Ставка берётся полем, а не пересчётом:
                              договорную сумму система помнит, а не
                              восстанавливает. */}
                          <Money frozen currency={o.currency} value={o.price} />
                          {', наценка '}
                          <Money
                            frozen
                            currency={o.currency}
                            value={toDeal(fee, o.rateToRub)}
                          />
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="order__read">
                <div className="divider" />

                {/*
                  * Кому звонить.
                  *
                  * Заказчику отдаётся диспетчер, а не водитель: водителя
                  * дёргают в дороге по любому поводу, он за рулём и не в
                  * курсе договорённостей. У частника без компании
                  * диспетчера нет — там остаётся его собственный контакт,
                  * и сервер это решает.
                  */}
                <div className="meta">
                  {o.myRole === 'DRIVER'
                    ? 'Заказчик: '
                    : o.contact.role === 'MANAGER'
                      ? 'Диспетчер: '
                      : 'Перевозчик: '}
                  {o.myRole === 'DRIVER'
                    ? [counterparty.firstName, counterparty.lastName]
                        .filter(Boolean)
                        .join(' ') || '—'
                    : o.contact.name}

                  {(() => {
                    const c = o.myRole === 'DRIVER' ? counterparty : o.contact;
                    return (
                      <>
                        {c.phone && (
                          <>
                            <br />
                            <a href={`tel:${c.phone}`}>{c.phone}</a>
                          </>
                        )}
                        {c.tgUsername && (
                          <>
                            {' · '}
                            <a href={`https://t.me/${c.tgUsername}`}>@{c.tgUsername}</a>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Чем везут. Заказчик на выгрузке узнаёт машину по цвету,
                    марке и номеру, а не по типу кузова. */}
                {o.vehicle && (
                  <div className="meta" style={{ marginTop: 4 }}>
                    Машина: {vehicleTitle(o.vehicle)}
                    {o.vehicle.bodyType
                      ? ` · ${BODY_TYPE_LABEL[o.vehicle.bodyType as BodyType]}`
                      : ''}
                  </div>
                )}

                {canAssign && (status === 'NEW' || status === 'LOADING') && (
                  <div className="field" style={{ marginTop: 12 }}>
                    <label>Кто едет</label>
                    <select
                      value={o.driverId}
                      onChange={async (e) => {
                        try {
                          await api.assignDriver(o.id, e.target.value);
                          await load();
                        } catch (err) {
                          await alert((err as Error).message);
                        }
                      }}
                    >
                      {drivers.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Компактно: сама трансляция — редкое и в остальном
                    объяснённое в боте состояние, и на каждой карточке
                    активного рейса полотном текста быть не должно. */}
                {o.myRole === 'DRIVER' &&
                  !o.lastPoint &&
                  status !== 'DONE' &&
                  status !== 'CANCELLED' && (
                    <div className="meta" style={{ marginTop: 8 }}>
                      Геопозиция не передаётся ·{' '}
                      <button
                        type="button"
                        className="linkish"
                        disabled={sendingPoint}
                        onClick={() => void sendPoint(o.id)}
                      >
                        {sendingPoint ? 'определяю…' : 'отметиться здесь'}
                      </button>
                    </div>
                  )}

                {o.cancelReason && (
                  <div className="error" style={{ marginTop: 12 }}>
                    Рейс отменён{o.cancelledByName ? `: ${o.cancelledByName}` : ''}
                    <div className="meta" style={{ marginTop: 4 }}>
                      Причина: {o.cancelReason}
                    </div>
                  </div>
                )}
              </div>

              <div className="order__act col">
                {/* Статусы перевозки отмечает только исполнитель: он один
                    знает, где машина. Хвостов у `next` после фильтра
                    CANCELLED не бывает больше одного. */}
                {canMove && next[0] && (
                  <button onClick={() => void move(o, next[0]!)}>
                    {ORDER_STATUS_LABEL[next[0]]}
                  </button>
                )}

                {/* Завершение отмечает исполнитель, приёмку — заказчик.
                    Без второй отметки спор о доставке остаётся открытым. */}
                {o.canConfirm && (
                  <>
                    <button
                      onClick={async () => {
                        if (!(await confirm('Груз доставлен и принят?'))) return;
                        try {
                          await api.confirmOrder(o.id);
                          notify('success');
                          await load();
                        } catch (e) {
                          await alert((e as Error).message);
                        }
                      }}
                    >
                      Подтвердить доставку
                    </button>
                    <div className="meta">
                      Исполнитель отметил рейс завершённым. Подтвердите приёмку —
                      после этого статус уже не изменится.
                    </div>
                  </>
                )}

                {o.confirmedAt && (
                  <div className="meta">
                    Доставка подтверждена{' '}
                    {new Date(o.confirmedAt).toLocaleDateString('ru-RU')}
                    {o.confirmedByName ? ` · ${o.confirmedByName}` : ''}
                  </div>
                )}

                {!closed(status) && o.myRole === 'LOGIST' && status !== 'NEW' && (
                  <div className="meta">
                    Рейс в работе — отменить может исполнитель или поддержка.
                  </div>
                )}

                <button className="secondary" onClick={() => void openDetails(o.id)}>
                  Подробности
                </button>

                {/* Редкое и необратимое — компактной ссылкой под основными
                    действиями, а не полосой во всю ширину наравне с ними. */}
                {canCancel && (
                  <div style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="linkish linkish--danger"
                      onClick={() => void move(o, 'CANCELLED')}
                    >
                      Отменить рейс
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Подробности рейса — окном, а не разворотом внутри списка: карта,
          история, файлы и расходы вместе способны растянуть карточку на
          несколько экранов, и искать соседний рейс под ними неудобно. */}
      <Modal
        open={open !== null}
        onClose={() => setOpenId(null)}
        title={open ? `${open.cargo.fromAddress} → ${open.cargo.toAddress}` : undefined}
      >
        {open && openMeta && (
          <>
            {/* Редкие операционные действия — здесь, а не в списке: их
                смотрят по конкретному поводу (поломка, ошибка статуса),
                а не при каждом взгляде на рейс. */}
            {(openMeta.canAssign || (openMeta.canMove && openMeta.back.length > 0)) && (
              <div className="action-row" style={{ marginBottom: 'var(--s3)' }}>
                {openMeta.canAssign &&
                  !closed(openMeta.status) && (
                    <button
                      className="secondary btn--compact"
                      onClick={() => {
                        setOpenId(null);
                        setReassign(open.id);
                      }}
                    >
                      Перегруз в другую машину
                    </button>
                  )}
                {openMeta.canMove &&
                  openMeta.back.map((s) => (
                    <button
                      key={s}
                      className="secondary btn--compact"
                      onClick={() => void move(open, s, true)}
                    >
                      ← {ORDER_STATUS_LABEL[s]}
                    </button>
                  ))}
              </div>
            )}

            {/* Договор-заявка отдельной строкой: это главный документ
                рейса, и искать его среди фотографий накладной неудобно. */}
            {files
              .filter((f) => f.kind === 'ORDER_PDF')
              .map((f) => (
                <div key={f.id} className="doc-file">
                  <Icon name="doc" size={18} />
                  <span className="col">
                    <strong>Договор-заявка</strong>
                    <span className="meta">PDF · формируется площадкой</span>
                  </span>
                  <DownloadButton file={f} />
                </div>
              ))}

            <div className="doc-row">
              <h3 style={{ margin: 0 }}>Документы рейса</h3>
              <UploadButton
                kind="POD"
                orderId={open.id}
                label="Накладная"
                onDone={(row) => setFiles((prev) => [...prev, row])}
              />
            </div>
            <div className="meta">Фото накладной и груза. Видят обе стороны рейса.</div>
            <FileGrid
              items={files.filter((f) => f.kind !== 'ORDER_PDF')}
              onDelete={(id) => setFiles((prev) => prev.filter((f) => f.id !== id))}
            />

            {/* Расходы ведёт тот, кто их несёт. Заказчику чужая книга не
                нужна, а своя живёт в разделе «Финансы». */}
            {open.myRole === 'DRIVER' && <Expenses orderId={open.id} />}

            {/* Заголовок отвечает на вопрос, который человек задаёт карте
                первым: это машина сейчас или где она была? Без отметки
                последняя точка суточной давности читается как «стоит
                здесь». */}
            <h3 className="trackhead">
              Где машина
              {open.trackLive ? (
                <span className="badge badge--live">трансляция идёт</span>
              ) : (
                track.length > 0 && (
                  <span className="meta">последняя точка {trackAgo(track)}</span>
                )
              )}
            </h3>
            {/* Пустое состояние здесь — забота самого TrackMap («Точек
                ещё нет — водитель не включил трансляцию.»), и повторять
                его отдельной строкой ниже значило бы сказать дважды одно
                и то же — что и происходило до этой правки. */}
            <TrackMap points={track} />

            <h3 style={{ marginTop: 16 }}>История</h3>
            <ol className="timeline">
              {history.map((e) => (
                <li key={e.id} className={e.isRollback ? 'is-rollback' : ''}>
                  <div>
                    <strong>{ORDER_STATUS_LABEL[e.toStatus] ?? e.toStatus}</strong>
                    {e.isRollback && (
                      <span className="badge badge--warn" style={{ marginLeft: 8 }}>
                        откат
                      </span>
                    )}
                  </div>
                  <div className="meta">
                    {new Date(e.createdAt).toLocaleString('ru-RU')} ·{' '}
                    {e.byMe ? 'вы' : e.byName}
                  </div>
                  {e.comment && <div className="meta">{e.comment}</div>}
                </li>
              ))}
            </ol>
          </>
        )}
      </Modal>

      {/* Перегруз оформляется отдельным окном: нужны и выбор, и причина,
          а причина здесь не формальность — по ней потом объясняют
          заказчику, почему на выгрузку пришла другая машина. */}
      <Modal open={reassign !== null} onClose={() => setReassign(null)} title="Перегруз">
        {reassign && (
          <ReassignForm
            orderId={reassign}
            drivers={drivers}
            onDone={async () => {
              setReassign(null);
              await load();
            }}
          />
        )}
      </Modal>
    </>
  );
}

/**
 * Передача рейса другой машине или водителю.
 *
 * Машины берутся из парка компании — того же, что виден во вкладке «Парк».
 * Чужую технику подставить нельзя: сервер проверяет принадлежность ещё раз,
 * но и предлагать её здесь незачем.
 */
function ReassignForm({
  orderId,
  drivers,
  onDone,
}: {
  orderId: string;
  drivers: { id: string; name: string }[];
  onDone: () => Promise<void>;
}) {
  const [companies, setCompanies] = useState<MyCompanyRow[]>([]);
  const [fleet, setFleet] = useState<FleetRow[]>([]);
  const [driverId, setDriverId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .myCompanies()
      .then((r) => {
        setCompanies(r.items);
        const managed = r.items.find(
          (c) => rolesManage(c.roles),
        );
        if (managed) {
          void api
            .fleet(managed.id)
            .then((f) => setFleet(f.items.filter((v) => !TOWED_KINDS.includes(v.kind))))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  async function submit() {
    if (reason.trim().length < 3) {
      await alert('Опишите причину: поломка, ДТП, замена по графику.');
      return;
    }
    if (!driverId && !vehicleId) {
      await alert('Выберите машину или водителя.');
      return;
    }

    setBusy(true);
    try {
      await api.reassignOrder(orderId, {
        ...(driverId ? { driverId } : {}),
        ...(vehicleId ? { vehicleId } : {}),
        reason: reason.trim(),
      });
      notify('success');
      await onDone();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="field">
        <label>Новая машина</label>
        <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
          <option value="">Оставить прежнюю</option>
          {fleet.map((v) => (
            <option key={v.id} value={v.id}>
              {v.plate} · {v.trip ? 'в рейсе' : 'свободна'}
            </option>
          ))}
        </select>
        {companies.length > 0 && fleet.length === 0 && (
          <div className="meta" style={{ marginTop: 4 }}>
            В парке компании нет свободной техники — заведите её во вкладке
            «Парк».
          </div>
        )}
      </div>

      <div className="field">
        <label>Новый водитель</label>
        <select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
          <option value="">Оставить прежнего</option>
          {drivers.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label>Причина</label>
        <textarea
          rows={2}
          placeholder="Поломка на трассе, груз перегружен"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="meta" style={{ marginTop: 4 }}>
          Попадёт в хронику рейса. Заказчик увидит другую машину на выгрузке
          и спросит, почему.
        </div>
      </div>

      <button disabled={busy} onClick={() => void submit()}>
        {busy ? 'Передаю…' : 'Передать рейс'}
      </button>
    </>
  );
}
